import React from "react";
import { apiErrorMessage } from "../../lib/apiErrorMessage";
import { Download, UploadCloud } from "lucide-react";
import type {
  BulkTransactionRecord,
  Channel,
  ReportFormat,
} from "@kkp/api-client";
import { PageContainer } from "@kkp/ui";
import { Button } from "@kkp/ui";
import { Card } from "@kkp/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/Dialog";
import { useQuery } from "@tanstack/react-query";
import {
  downloadBulkReport,
  downloadBulkTemplate,
  listBulkTransfers,
  validateBulkTransfer,
  uploadBulkTransfer,
} from "../../api/transactions";
import { toast } from "@kkp/ui";
import { formatCurrency, formatNumber } from "@kkp/app-kit";
import { BulkHistoryRows } from "./BulkHistoryRows";
import { PaymentDestinationBlock } from "./PaymentDestinationBlock";

const NextBulkHistoryRows = React.lazy(() => import("./NextBulkHistoryRows"));

export default function TransactionsPage() {
  const { data, refetch } = useQuery({
    queryKey: ["bulk-transfers"],
    queryFn: listBulkTransfers,
    staleTime: 0,
  });
  const transactions = data ?? [];
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [selectedChannel, setSelectedChannel] = React.useState<Channel>("Bank");
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [validation, setValidation] = React.useState<{
    // MoneyIdr strings straight from the validation summary (no client-side math).
    totalAmount: string;
    totalFee: string;
    totalReceivable: string;
    totalRecipients: string;
  } | null>(null);
  const [uploadKey, setUploadKey] = React.useState<string | null>(null);
  const [isValidating, setIsValidating] = React.useState(false);
  const [fileInputKey, setFileInputKey] = React.useState(0);

  const vaInputRef = React.useRef<HTMLInputElement | null>(null);
  const bankInputRef = React.useRef<HTMLInputElement | null>(null);

  const resetFileInputs = () => {
    if (vaInputRef.current) vaInputRef.current.value = "";
    if (bankInputRef.current) bankInputRef.current.value = "";
  };

  const handleDownloadTemplate = () => {
    downloadBulkTemplate()
      .then((csv) => {
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "bulk-transfer-template.csv";
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => {
        toast.danger("Failed to download template.", {
          title: "Bulk transfer",
        });
      });
  };

  const handleUpload = (channel: Channel) => {
    setSelectedChannel(channel);
    if (channel === "VA") {
      if (vaInputRef.current) vaInputRef.current.value = "";
      vaInputRef.current?.click();
    } else {
      if (bankInputRef.current) bankInputRef.current.value = "";
      bankInputRef.current?.click();
    }
  };

  const handleUploadSelected = (
    channel: Channel,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;
    setSelectedChannel(channel);
    setSelectedFile(file);
    setIsValidating(true);
    validateBulkTransfer(channel, file)
      .then((summary) => {
        setValidation({
          totalAmount: summary.totalAmount,
          totalFee: summary.totalFee,
          totalReceivable: summary.totalReceivable,
          totalRecipients: summary.totalRecipients,
        });
        setUploadKey(crypto.randomUUID());
        setConfirmOpen(true);
      })
      .catch((err) => {
        const message = apiErrorMessage(
          err,
          "Validation failed. Please check your CSV.",
        );
        toast.danger(message, {
          title: "Bulk transfer",
        });
        setSelectedFile(null);
        setValidation(null);
        setConfirmOpen(false);
      })
      .finally(() => {
        setIsValidating(false);
        input.value = "";
        setFileInputKey((key) => key + 1);
      });
  };

  const handleConfirmTransfer = () => {
    if (!selectedFile || !uploadKey) return;
    uploadBulkTransfer(selectedChannel, selectedFile, uploadKey)
      .then(() => {
        setUploadKey(null);
        setConfirmOpen(false);
        setSelectedFile(null);
        setValidation(null);
        resetFileInputs();
        setFileInputKey((key) => key + 1);
        void refetch();
        toast.success("Bulk transfer queued.", { title: "Bulk transfer" });
      })
      .catch((err) => {
        const message = apiErrorMessage(err, "Failed to upload bulk transfer.");
        toast.danger(message, {
          title: "Bulk transfer",
        });
        setSelectedFile(null);
        setValidation(null);
        setConfirmOpen(false);
        resetFileInputs();
        setFileInputKey((key) => key + 1);
      });
  };

  const handleDownloadReport = (
    record: BulkTransactionRecord,
    format: ReportFormat,
  ) => {
    downloadBulkReport(record.bulkTransactionId, format)
      .then((payload) => {
        const blob =
          format === "pdf"
            ? (payload as Blob)
            : new Blob([payload as string], {
                type: "text/csv;charset=utf-8;",
              });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `bulk-transaction-${record.bulkTransactionId}.${format}`;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => {
        toast.danger("Failed to download report.", {
          title: "Bulk transfer",
        });
      });
  };

  return (
    <PageContainer
      title="Transactions"
      description="Upload bulk transfer CSVs and track transaction history."
      width="full"
    >
      <div className="grid gap-4">
        <Card className="grid gap-3">
          <div className="text-sm font-semibold text-white">
            Transfer limits (per bulk transaction)
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <div className="text-xs text-[color:var(--kkp-text-muted)]">
                VA Limit
              </div>
              <div className="mt-1 text-lg font-semibold text-white">
                {formatCurrency("100000000", "id-ID", "IDR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
            <div>
              <div className="text-xs text-[color:var(--kkp-text-muted)]">
                Bank Transfer Limit
              </div>
              <div className="mt-1 text-lg font-semibold text-white">
                {formatCurrency("2000000000", "id-ID", "IDR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          </div>
        </Card>

        <Card className="grid gap-3">
          <div className="text-sm font-semibold text-white">
            Bulk transfer actions
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={handleDownloadTemplate}>
              <Download className="h-4 w-4" />
              Download Bulk Transfer Template
            </Button>
            <Button variant="primary" disabled>
              <UploadCloud className="h-4 w-4" />
              Transfer via VA
            </Button>
            <Button variant="primary" onClick={() => handleUpload("Bank")}>
              <UploadCloud className="h-4 w-4" />
              Transfer via Bank
            </Button>
          </div>
          <div className="text-xs text-[color:var(--kkp-text-muted)]">
            Template includes all active recipients.
          </div>
        </Card>

        <Card className="p-0 overflow-hidden">
          <div className="flex flex-col gap-2 border-b border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(255,255,255,0.02)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-semibold text-white">
                Bulk transaction history
              </div>
              <div className="text-xs text-[color:var(--kkp-text-muted)]">
                {transactions.length} historical bulk transaction(s).
              </div>
            </div>
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              Refresh
            </Button>
          </div>

          <div className="overflow-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[color:var(--kkp-surface-2)]">
                <tr className="text-[11px] font-bold uppercase tracking-wide text-[color:rgba(255,255,255,0.75)]">
                  <th className="px-4 py-3">Seq ID</th>
                  <th className="px-4 py-3">Bulk Transaction ID</th>
                  <th className="px-4 py-3">Created At</th>
                  <th className="px-4 py-3">Executed At</th>
                  <th className="px-4 py-3">Total Receivable</th>
                  <th className="px-4 py-3">Total Recipients</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                <React.Suspense
                  fallback={
                    <BulkHistoryRows
                      transactions={transactions}
                      onDownloadReport={handleDownloadReport}
                    />
                  }
                >
                  <NextBulkHistoryRows
                    transactions={transactions}
                    onDownloadReport={handleDownloadReport}
                    onConfirmed={() => {
                      void refetch();
                    }}
                  />
                </React.Suspense>
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <input
        key={`bulk-upload-va-${fileInputKey}`}
        ref={vaInputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={(event) => handleUploadSelected("VA", event)}
      />
      <input
        key={`bulk-upload-bank-${fileInputKey}`}
        ref={bankInputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={(event) => handleUploadSelected("Bank", event)}
      />

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm bulk transfer ({selectedChannel})</DialogTitle>
            <DialogDescription>
              Please review the summary before confirming this bulk transfer.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            <PaymentDestinationBlock channel={selectedChannel} />
          </div>

          <div className="mt-4 grid gap-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[color:var(--kkp-text-muted)]">
                Total Amount to Pay
              </span>
              <span className="font-semibold text-white">
                {formatCurrency(
                  validation?.totalAmount ?? "0",
                  "id-ID",
                  "IDR",
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  },
                )}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[color:var(--kkp-text-muted)]">
                Total Fee
              </span>
              <span className="font-semibold text-white">
                {formatCurrency(validation?.totalFee ?? "0", "id-ID", "IDR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[color:var(--kkp-text-muted)]">
                Total Receivable
              </span>
              <span className="font-semibold text-white">
                {formatCurrency(
                  validation?.totalReceivable ?? "0",
                  "id-ID",
                  "IDR",
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  },
                )}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[color:var(--kkp-text-muted)]">
                Total Recipients
              </span>
              <span className="font-semibold text-white">
                {formatNumber(validation?.totalRecipients ?? 0)}
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmTransfer}
              disabled={!validation || isValidating}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
